import { DownloadOutlined } from '@ant-design/icons';
import { Button, Collapse, Table } from 'antd';
import dayjs from 'dayjs';
import { formatCsvDate } from 'modules/dashboard/utils/csvDate';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getColumns } from './columns';
import DonationDetailModal from './DonationDetailModal';
import styles from './styles.module.scss';

import { DonationRecord } from '../../../types/phlebotomist-dashboard';
import { PhlebTooltip } from '../../PhlebTooltip';

const { Panel } = Collapse;

interface IRecentDonationsTableProps {
  donations: DonationRecord[];
  loading?: boolean;
  defaultExpanded?: boolean;
  showPhlebotomist?: boolean;
  /** Measured pane width from parent - used for responsive calculations */
  paneWidth?: number;
  /** File name for CSV export (without .csv extension) */
  exportFileName?: string;
}

export const RecentDonationsTable: FC<IRecentDonationsTableProps> = ({
  donations,
  loading = false,
  defaultExpanded = true,
  showPhlebotomist = false,
  paneWidth,
  exportFileName,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');
  const [selectedDonation, setSelectedDonation] =
    useState<DonationRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Break to mobile when pane width < 1120px
  // Default to non-mobile if paneWidth not provided
  const isMobile = paneWidth !== undefined ? paneWidth < 1120 : false;

  const columns = useMemo(
    () => getColumns(t, { showPhlebotomist, isMobile, donations }),
    [t, showPhlebotomist, isMobile, donations]
  );

  // Scroll width matches pane breakpoint
  const scrollX = isMobile ? 748 : 1010;

  const failureReasonLabels: Record<string, string> = {
    Clots: 'Clots',
    LowVolume: 'Low Volume',
    Hemolyzed: 'Hemolyzed',
    SealIssue: 'Seal Issue',
    BrokenBag: 'Broken Bag',
    RedCellContamination: 'Red Cell Contamination',
    DonorHealthIssue: 'Donor Health Issue',
    Lipemic: 'Lipemic',
    Other: 'Other',
    Timeout: 'Unsuccessful',
    Expired: 'Expired',
  };

  const exportCsv = useCallback(() => {
    const headers = [
      'UUID',
      'Short ID',
      'Date',
      'Donor',
      ...(showPhlebotomist ? ['Phlebotomist'] : []),
      'Location',
      'Region',
      'Type',
      'Approved ML',
      'Exp pRBC',
      'Act pRBC',
      'Exp FFP',
      'Act FFP',
      'Status',
      'Failure Reason',
      'Examination Notes',
    ];

    const rows = donations.map((d) => [
      d.id,
      String(d.shortId),
      formatCsvDate(d.date),
      d.donorName,
      ...(showPhlebotomist ? [d.phlebotomistName] : []),
      d.locationName,
      d.regionName,
      d.type,
      d.approvedML > 0 ? String(d.approvedML) : '',
      d.expectedPRBC ? String(d.expectedPRBC) : '',
      d.actualPRBC ? String(d.actualPRBC) : '',
      d.expectedFFPDouble
        ? `${d.expectedFFPDouble} DBL`
        : d.expectedFFPSingle
        ? `${d.expectedFFPSingle} SGL`
        : '',
      d.actualFFPDouble
        ? `${d.actualFFPDouble} DBL`
        : d.actualFFPSingle
        ? `${d.actualFFPSingle} SGL`
        : '',
      d.status,
      (d.failureReasons || [])
        .map((r) => failureReasonLabels[r] || r)
        .join(', '),
      d.examinationNotes || '',
    ]);

    const escape = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const csv = [headers, ...rows]
      .map((row) => row.map(escape).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${
      exportFileName || `donations-${dayjs().format('YYYY-MM-DD')}`
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [donations, showPhlebotomist]);

  // Add unique keys to donations
  const dataSource = useMemo(
    () =>
      donations.map((d, index) => ({
        ...d,
        key: d.id || `donation-${index}`,
      })),
    [donations]
  );

  const panelHeader = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <PhlebTooltip text="Chronological list of all donation draws during the selected period. Each row shows the donor, location, type (SGL/DBL), expected vs actual product units for pRBC and FFP, donation status, and any failure reasons. Click a row for full details.">
        <span>{t('table.title', 'Recent Donations/Draws')}</span>
      </PhlebTooltip>
      <Button
        size="small"
        icon={<DownloadOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          exportCsv();
        }}
      >
        Export CSV
      </Button>
    </div>
  );

  return (
    <>
      <Collapse
        defaultActiveKey={defaultExpanded ? ['donations'] : []}
        style={{ background: 'white' }}
      >
        <Panel header={panelHeader} key="donations">
          <Table
            className={styles.tableWrapper}
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            onRow={(record) => ({
              onClick: (e) => {
                e.stopPropagation();
                setSelectedDonation(record as DonationRecord);
                setModalOpen(true);
              },
              style: { cursor: 'pointer' },
            })}
            pagination={{
              defaultPageSize: 10,
              pageSizeOptions: ['10', '20', '50', '100'],
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                t('table.pagination', '{{start}}-{{end}} of {{total}} items', {
                  start: range[0],
                  end: range[1],
                  total,
                }),
            }}
            scroll={{ x: scrollX }}
            size="small"
          />
        </Panel>
      </Collapse>

      <DonationDetailModal
        donation={selectedDonation}
        visible={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedDonation(null);
        }}
      />
    </>
  );
};

export default RecentDonationsTable;
